/* Collection page -- lightweight custom controller (no Dawn facets.js).
   Handles: sticky toolbar that hides on scroll-down / reveals on
   scroll-up, the shared right-side filter drawer (accordion sections,
   opened + auto-expanded by any pill), filter/sort AJAX updates via
   Shopify's Section Rendering API, dual price slider, client-side brand
   search, floating quick-add-to-cart on single-variant product cards,
   and "load more" pagination with optional auto-infinite-scroll.
   Works with JS disabled too: the filter form is a real GET form and the
   load-more button is a real link -- this file only intercepts to make
   it faster when JS is available. */
(function () {
  'use strict';

  function wireQuickAdd(btn) {
    if (!btn || btn.dataset.cpWired === '1') return;
    btn.dataset.cpWired = '1';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (btn.classList.contains('is-loading') || btn.classList.contains('is-added')) return;

      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.classList.add('is-loading');
      var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      if (rootPath.slice(-1) !== '/') rootPath += '/';

      fetch(rootPath + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (data) { throw new Error(data.description || 'Could not add to cart'); });
          return res.json();
        })
        .then(function () {
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(function () {
            btn.classList.remove('is-added');
          }, 1800);
        })
        .catch(function () {
          btn.classList.remove('is-loading');
        });
    });
  }

  function initSection(root) {
    if (!root || root.dataset.cpInit === '1') return;
    root.dataset.cpInit = '1';

    var sectionId = root.dataset.sectionId;
    var toolbar = root.querySelector('[data-collection-toolbar]');
    var drawer = root.querySelector('[data-filter-drawer]');
    var form = root.querySelector('#CollectionFacetsForm');
    var grid = root.querySelector('.collection-page__grid');
    var count = root.querySelector('.collection-page__count');
    var loadMoreWrap = root.querySelector('.collection-page__load-more');
    var loadMoreBtn = root.querySelector('.collection-page__load-btn');
    var sentinel = root.querySelector('.collection-page__sentinel');
    var isFetching = false;

    /* Every page of results Shopify hands us is already in-stock-first
       *within that page*. "Load more"/auto-scroll appends more pages
       onto the grid, so this re-sorts the FULL set of cards currently in
       the DOM after every append: in-stock cards move to the front,
       sold-out cards move to the back (never removed -- kept for SEO). */
    function resortGrid() {
      if (!grid) return;
      var items = Array.prototype.slice.call(grid.children);
      if (!items.length) return;
      var inStock = [];
      var soldOut = [];
      items.forEach(function (li) {
        if (li.querySelector('.collection-page__badge--sold')) {
          soldOut.push(li);
        } else {
          inStock.push(li);
        }
      });
      inStock.concat(soldOut).forEach(function (li) {
        grid.appendChild(li);
      });
    }

    // ---- Sticky toolbar: hide on scroll-down, reveal on scroll-up ----
    if (toolbar) {
      var lastY = window.scrollY;
      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        if (y > lastY && y > 120) {
          toolbar.classList.add('is-hidden');
        } else {
          toolbar.classList.remove('is-hidden');
        }
        lastY = y;
      }, { passive: true });
    }

    // ---- Shared right-side filter drawer ----
    if (drawer) {
      var drawerBackdrop = drawer.querySelector('[data-filter-drawer-backdrop]');
      var drawerClose = drawer.querySelector('[data-filter-drawer-close]');
      var accordionGroups = drawer.querySelectorAll('[data-accordion]');

      function openDrawer(filterIndex) {
        drawer.hidden = false;
        document.body.classList.add('collection-filters-locked');
        requestAnimationFrame(function () {
          drawer.classList.add('is-open');
        });
        accordionGroups.forEach(function (g) {
          var isTarget = filterIndex !== undefined && g.dataset.filterIndex === String(filterIndex);
          g.classList.toggle('is-open', isTarget);
        });
        if (filterIndex !== undefined) {
          var targetGroup = drawer.querySelector('[data-accordion][data-filter-index="' + filterIndex + '"]');
          if (targetGroup) {
            setTimeout(function () {
              targetGroup.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }, 120);
          }
        }
      }

      function closeDrawer() {
        drawer.classList.remove('is-open');
        document.body.classList.remove('collection-filters-locked');
        setTimeout(function () {
          drawer.hidden = true;
        }, 320);
      }

      root.querySelectorAll('[data-filter-drawer-trigger]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var idx = btn.dataset.filterIndex;
          openDrawer(idx === undefined ? undefined : idx);
        });
      });
      if (drawerBackdrop) drawerBackdrop.addEventListener('click', closeDrawer);
      if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
      });

      accordionGroups.forEach(function (g) {
        var head = g.querySelector('[data-accordion-trigger]');
        if (!head) return;
        head.addEventListener('click', function () {
          g.classList.toggle('is-open');
        });
      });

      /* Restore the drawer's open/expanded state across an AJAX filter
         update (see triggerUpdate below) -- checking a box re-renders
         this whole section from the server (closed-by-default markup),
         so without this the drawer would silently slam shut on every
         filter click instead of staying open while browsing options. */
      if (root.dataset.cpReopenDrawer !== undefined) {
        var reopenIdx = root.dataset.cpReopenDrawer === '1' ? undefined : root.dataset.cpReopenDrawer;
        delete root.dataset.cpReopenDrawer;
        openDrawer(reopenIdx);
      }
    }

    // ---- Brand / long list client-side search ----
    root.querySelectorAll('[data-filter-search]').forEach(function (input) {
      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        var list = input.closest('[data-accordion-body]').querySelector('.collection-page__filter-list');
        if (!list) return;
        list.querySelectorAll('.collection-page__filter-option').forEach(function (opt) {
          var label = (opt.dataset.label || '').toLowerCase();
          opt.classList.toggle('is-hidden', q.length > 0 && label.indexOf(q) === -1);
        });
      });
    });

    // ---- Dual price range slider ----
    root.querySelectorAll('[data-price-slider]').forEach(function (slider) {
      var minRange = slider.querySelector('[data-range-min]');
      var maxRange = slider.querySelector('[data-range-max]');
      var minField = slider.parentElement.querySelector('[data-price-min]');
      var maxField = slider.parentElement.querySelector('[data-price-max]');
      var fill = slider.querySelector('[data-price-fill]');
      var bounds = parseFloat(slider.dataset.max || '0') || 0;

      function updateFill() {
        if (!fill || !bounds) return;
        var lo = parseFloat(minRange.value) || 0;
        var hi = parseFloat(maxRange.value) || bounds;
        fill.style.left = (lo / bounds * 100) + '%';
        fill.style.right = (100 - (hi / bounds * 100)) + '%';
      }

      if (minRange && maxRange) {
        minRange.addEventListener('input', function () {
          if (parseFloat(minRange.value) > parseFloat(maxRange.value)) minRange.value = maxRange.value;
          if (minField) minField.value = minRange.value;
          updateFill();
        });
        maxRange.addEventListener('input', function () {
          if (parseFloat(maxRange.value) < parseFloat(minRange.value)) maxRange.value = minRange.value;
          if (maxField) maxField.value = maxRange.value;
          updateFill();
        });
        [minRange, maxRange].forEach(function (r) {
          r.addEventListener('change', function () {
            triggerUpdate();
          });
        });
        updateFill();
      }
      if (minField) {
        minField.addEventListener('change', function () {
          if (minRange) { minRange.value = minField.value || 0; updateFill(); }
          triggerUpdate();
        });
      }
      if (maxField) {
        maxField.addEventListener('change', function () {
          if (maxRange) { maxRange.value = maxField.value || bounds; updateFill(); }
          triggerUpdate();
        });
      }
    });

    // ---- Filter / sort AJAX ----
    function buildParams() {
      if (!form) return '';
      return new URLSearchParams(new FormData(form)).toString();
    }

    function triggerUpdate(pushState) {
      if (!form || !sectionId) return;
      var params = buildParams();
      isFetching = true;
      if (grid) grid.classList.add('is-loading');
      if (count) count.classList.add('is-loading');
      var url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) + (params ? '&' + params : '');

      /* The AJAX swap below replaces the drawer element wholesale with a
         fresh (closed-by-default) server render. If the drawer was open
         when the filter changed, remember which accordion group was
         open so it can be restored -- and release the body scroll lock
         *now*, since it lives on <body> (outside `root`) and would
         otherwise survive the innerHTML swap and leave the page stuck
         unscrollable until a manual refresh. */
      var wasDrawerOpen = !!(drawer && drawer.classList.contains('is-open'));
      var reopenFilterIndex;
      if (wasDrawerOpen) {
        var openGroup = drawer.querySelector('[data-accordion].is-open');
        reopenFilterIndex = openGroup ? openGroup.dataset.filterIndex : undefined;
      }
      document.body.classList.remove('collection-filters-locked');

      fetch(url)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newRoot = doc.querySelector('[data-collection-page]');
          if (!newRoot) return;
          root.dataset.cpInit = '';
          if (wasDrawerOpen) root.dataset.cpReopenDrawer = reopenFilterIndex === undefined ? '1' : reopenFilterIndex;
          root.innerHTML = newRoot.innerHTML;
          initSection(root);
        })
        .catch(function () {
          if (form) form.submit();
        })
        .finally(function () {
          isFetching = false;
          if (pushState !== false) {
            var shallowUrl = window.location.pathname + (params ? '?' + params : '');
            window.history.pushState({ cp: params }, '', shallowUrl);
          }
        });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        triggerUpdate(true);
      });
      form.querySelectorAll('input[type=checkbox]').forEach(function (cb) {
        cb.addEventListener('change', function () { triggerUpdate(true); });
      });
    }
    /* Sort <select> lives in the toolbar, outside the <form> DOM tree --
       it's associated via the HTML `form` attribute (still included in
       FormData(form)), so it must be found by searching the whole section. */
    root.querySelectorAll('[data-sort-select]').forEach(function (select) {
      select.addEventListener('change', function () { triggerUpdate(true); });
    });

    root.querySelectorAll('[data-clear-filters]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        window.location = btn.getAttribute('href') || window.location.pathname;
      });
    });

    // ---- Floating quick-add (single-variant products only) ----
    root.querySelectorAll('[data-quick-add]').forEach(wireQuickAdd);

    // ---- Load more ----
    function loadMore(url) {
      if (!url || isFetching) return;
      isFetching = true;
      if (loadMoreBtn) loadMoreBtn.classList.add('is-loading');
      var fetchUrl = url.indexOf('section_id=') === -1
        ? url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId)
        : url;

      fetch(fetchUrl)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newItems = doc.querySelectorAll('.collection-page__grid > li');
          if (grid && newItems.length) {
            newItems.forEach(function (li) {
              grid.appendChild(li);
              li.querySelectorAll('[data-quick-add]').forEach(wireQuickAdd);
            });
            resortGrid();
          }
          var newLoadMore = doc.querySelector('.collection-page__load-more');
          if (loadMoreWrap && newLoadMore) {
            var nextHasBtn = newLoadMore.querySelector('.collection-page__load-btn');
            if (nextHasBtn) {
              if (loadMoreBtn) {
                loadMoreBtn.href = nextHasBtn.getAttribute('href');
                loadMoreBtn.classList.remove('is-loading');
              }
            } else {
              loadMoreWrap.remove();
            }
          }
        })
        .catch(function () {
          if (loadMoreBtn) loadMoreBtn.classList.remove('is-loading');
        })
        .finally(function () {
          isFetching = false;
        });
    }

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function (e) {
        e.preventDefault();
        loadMore(loadMoreBtn.getAttribute('href'));
      });
    }

    if (sentinel && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && loadMoreBtn && loadMoreBtn.getAttribute('href') && !isFetching) {
            loadMore(loadMoreBtn.getAttribute('href'));
          }
        });
      }, { rootMargin: '600px 0px' });
      io.observe(sentinel);
    }

    // Initial page load is already in-stock-first from Liquid, but run once
    // more so this function's own logic is the single source of truth.
    resortGrid();
  }

  function initAll() {
    document.querySelectorAll('[data-collection-page]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('popstate', function () {
    window.location.reload();
  });
})();
